import { PhoneCall, User } from "lucide-react";
import type { VoiceBridgeStatus } from "@/services/integrations/twilio/VoiceBridgeService";

interface HumanCallAvatarProps {
  brandColor: string;
  status: VoiceBridgeStatus;
  size?: number;
  embedded?: boolean;
  className?: string;
}

/**
 * Replaces the genie avatar while the visitor is on a live human call --
 * unmistakably different from the genie's own animated states, so it reads
 * as "you're talking to a person now," not a variant of the AI thinking/
 * speaking states.
 */
export const HumanCallAvatar = ({ brandColor, status, size, embedded = false, className }: HumanCallAvatarProps) => {
  const isLive = status === "in-call";
  const isConnecting = status === "requesting-token" || status === "connecting";

  const responsive = size
    ? { width: `${size}px`, height: `${size}px`, maxWidth: `${size}px`, maxHeight: `${size}px` }
    : embedded
      ? { width: "min(220px, 56vw)", height: "min(220px, 56vw)", maxWidth: "240px", maxHeight: "240px" }
      : { width: "min(400px, 80vw)", height: "min(400px, 80vw)", maxWidth: "400px", maxHeight: "400px" };

  const wrapperStyle: React.CSSProperties = {
    ...responsive,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    background: `${brandColor}20`,
    boxShadow: isLive ? `0 0 30px ${brandColor}40` : `0 0 15px ${brandColor}20`,
  };

  const ringStyle: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    borderRadius: "50%",
    border: `2px solid ${brandColor}`,
    opacity: 0.5,
    animation: isLive || isConnecting ? "humanCallPulse 1.6s ease-out infinite" : undefined,
  };

  return (
    <div style={wrapperStyle} className={className}>
      <style>{`
        @keyframes humanCallPulse {
          0% { transform: scale(1); opacity: 0.6; }
          100% { transform: scale(1.35); opacity: 0; }
        }
      `}</style>
      <div style={ringStyle} />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "40%",
          height: "40%",
          borderRadius: "50%",
          background: brandColor,
          color: "white",
        }}
      >
        {isLive ? <PhoneCall size={32} strokeWidth={2} /> : <User size={32} strokeWidth={2} />}
      </div>
    </div>
  );
};

export default HumanCallAvatar;
